import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task, waitForProperty } from 'ember-concurrency';
// eslint-disable-next-line ember/use-ember-data-rfc-395-imports
import type DS from 'ember-data';
import config from 'garaje/config/environment';
import { OauthProvider } from 'garaje/graphql/generated/roomba-types';
import type {
  Connection,
  GetCompanyQuery,
  GetRoomsLocationWithConfigsQuery,
  GetRoomsQuery,
  GetSpaceSaverConfigQuery,
} from 'garaje/graphql/generated/roomba-types';
import type PluginModel from 'garaje/models/plugin';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type RoombaGraphqlService from 'garaje/services/roomba-graphql';
import type RoombaMetricsService from 'garaje/services/roomba-metrics';
import type StateService from 'garaje/services/state';
import type StoreService from 'garaje/services/store';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { localCopy } from 'tracked-toolbox';

interface RoombaSettingsComponentArgs {
  model: {
    company: GetCompanyQuery['company'];
    roomLocationConfig: GetRoomsLocationWithConfigsQuery['location'];
    currentLocation: StateService['currentLocation'];
    spaceSaverConfig: GetSpaceSaverConfigQuery['spaceSaverConfig'];
    plugins: DS.RecordArray<PluginModel>;
    rooms: GetRoomsQuery['rooms'];
  };
}

export default class RoombaSettingsComponent extends Component<RoombaSettingsComponentArgs> {
  @service declare featureConfig: FeatureConfigService;
  @service declare flashMessages: FlashMessagesService;
  @service declare roombaGraphql: RoombaGraphqlService;
  @service declare roombaMetrics: RoombaMetricsService;
  @service declare state: StateService;
  @service declare store: StoreService;

  @localCopy('args.model.company.settingsAccessCode') declare settingsAccessCode: string;

  @tracked selectedDeleteConnection: Connection | null = null;
  @tracked deleteConnectionShouldRemoveRooms = false;

  get googleConnections(): GetCompanyQuery['company']['connections'] {
    return (this.args.model.company?.connections || []).filter(
      (connection) => connection.provider === OauthProvider.Google,
    );
  }

  get oauthProviders(): typeof OauthProvider {
    return OauthProvider;
  }

  get roomNotificationSettingsActive(): boolean {
    return this.featureConfig.isEnabled('roomMeetings.notifications');
  }

  get hasLinkedLocation(): GetCompanyQuery['company']['locations'] {
    return (this.args.model.company?.locations || []).filter((loc) => loc.id === this.state.currentLocation?.id);
  }

  get canPairDevices(): boolean {
    const connections = this.args.model.company?.connections || [];
    const hasConnectedRooms = connections.some(
      (connection) => connection.roomsCount != null && connection.roomsCount > 0,
    );

    return connections.length > 0 && hasConnectedRooms;
  }

  get shouldShowLinkLocationComponent(): boolean {
    return config.environment === 'development';
  }

  refreshRoomList = task({ drop: true }, async () => {
    try {
      this.roombaMetrics.trackEvent('calendar_settings_refresh_calendars_clicked', {
        locationId: this.state.currentLocation?.id,
      });

      const { errors } = await this.roombaGraphql.synchronizeCompanyRoomList();
      if (errors.length === 0) {
        this.flashMessages.showAndHideFlash('success', 'Calendars synced!');
      } else {
        this.flashMessages.showAndHideFlash('warning', 'Encountered some errors while syncing calendars.');
        console.error('refreshRoomList errors:', errors); // eslint-disable-line no-console
      }
    } catch {
      this.flashMessages.showAndHideFlash('error', 'Error syncing calendars.');
    }

    this.selectedDeleteConnection = null;
    this.deleteConnectionShouldRemoveRooms = false;
  });

  deleteConnection = task({ drop: true }, async () => {
    try {
      this.roombaMetrics.trackEvent('calendar_settings_remove_calendar_clicked', {
        locationId: this.state.currentLocation?.id,
      });

      if (this.selectedDeleteConnection != null) {
        await this.roombaGraphql.unlinkConnection({
          connectionId: this.selectedDeleteConnection.id,
          shouldDeleteAssociatedRooms: this.deleteConnectionShouldRemoveRooms,
        });
        this.flashMessages.showAndHideFlash('success', 'Calendar removed!');
      } else {
        this.flashMessages.showAndHideFlash('error', 'Error removing calendar account.');
      }
    } catch {
      this.flashMessages.showAndHideFlash('error', 'Error removing calendar account.');
    }

    this.selectedDeleteConnection = null;
    this.deleteConnectionShouldRemoveRooms = false;
  });

  saveSettingAccessCode = task({ drop: true }, async (code: string) => {
    if (code.length < 4) {
      return;
    }
    try {
      const result = await this.roombaGraphql.updateCompanyConfig({
        settingsAccessCode: code,
      });
      this.settingsAccessCode = result.settingsAccessCode;
      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
      throw e;
    }
  });

  onBlurSettingAccessCode = task({ drop: true }, async () => {
    await waitForProperty(this, 'saveSettingAccessCode.isIdle', () => true);
    this.settingsAccessCode = this.args.model.company?.settingsAccessCode || '';
  });

  handleLinkLocationTask = task({ drop: true }, async () => {
    if (!this.state.currentLocation) {
      return;
    }

    try {
      await this.roombaGraphql.createLocation({
        id: this.state.currentLocation.id,
        name: this.state.currentLocation.name,
        companyId: this.args.model.company?.id || '',
      });
      this.flashMessages.showAndHideFlash('success', 'Linked!');
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
      throw e;
    }
  });

  // Only numbers for settings access code
  @action
  validateSettingAccessCode({ target }: { target: HTMLInputElement }): void {
    target.value = target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
  }
}
